import { useParams, useNavigate } from "react-router-dom";
import { useAirportDetails } from "@/hooks/useAirportDetails";
import { AirportHeader } from "@/components/airport/AirportHeader";
import { AirportStats } from "@/components/airport/AirportStats";
import { ReviewsList } from "@/components/airport/ReviewsList";
import { AirportDetailsSkeleton } from "@/components/airport/AirportDetailsSkeleton";
import { AirportNotFound } from "@/components/airport/AirportNotFound";
import { FavoriteButton } from "@/components/airport/FavoriteButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion, AnimatePresence } from "framer-motion";

export default function AirportDetails() {
  const { code } = useParams<{ code: string }>();
  const { 
    airport, 
    reviews, 
    airportStats, 
    isLoading, 
    isFavorited, 
    setIsFavorited 
  } = useAirportDetails(code);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleAddReview = () => {
    navigate('/new-review', {
      state: {
        airport: {
          id: airport?.id,
          name: airport?.name,
          location: airport?.location
        },
        previousAirportCode: airport?.iata_code
      }
    });
  };
  
  const handleFavoriteToggle = (newState: boolean) => {
    setIsFavorited(newState);
  };

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`container mx-auto ${isMobile ? 'px-3' : 'px-4'} py-4`}
        >
          <AirportDetailsSkeleton />
        </motion.div>
      ) : !airport ? (
        <motion.div
          key="not-found"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`container mx-auto ${isMobile ? 'px-3' : 'px-4'} py-4`}
        >
          <AirportNotFound />
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={`container mx-auto ${isMobile ? 'px-3 pb-20' : 'px-4'} py-4 max-w-full overflow-hidden`}
        >
          <div className={`${isMobile ? 'space-y-3' : 'space-y-6'}`}>
            <div className="w-full">
              <AirportHeader 
                name={airport.name}
                iata_code={airport.iata_code}
                location={airport.location}
                favoriteButton={
                  <FavoriteButton 
                    airportId={airport.id}
                    initialFavorited={isFavorited}
                    onToggle={handleFavoriteToggle}
                    className="ml-2"
                  />
                }
              />
            </div>

            {airportStats && (
              <div className={`${isMobile ? 'mx-0' : ''}`}>
                <AirportStats
                  rank={airportStats.rank}
                  handCheckRate={airportStats.handCheckRate}
                  totalReviews={airportStats.totalReviews}
                />
              </div>
            )}
            
            <ReviewsList
              reviews={reviews}
              onAddReview={handleAddReview}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
